import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import { CustomLabelFunction, Includes } from '@/types/common.type'
import { ConfigurationRelationshipsState, initialConfigurationRelationshipsState } from '@/modules/configurationModule/shared/components/configuration-relationships/configuration-relationships.model'

@Component
export default class ConfigurationRelationships<Option extends object = any, Relationship extends object & Includes<Relationship, Option> = any> extends Vue {
    /// SELECT PROPS ///

    /**
     * Check if duplicated options are allowed
     */
    @Prop({
        type: Boolean,
        default: false
    })
    readonly allowDuplicatedOptions!: boolean

    /**
     * Array of available options
     */
    @Prop({
        type: Array
    })
    readonly options!: Array<Option>

    /**
     * Function to resolve a promise of array of available options
     */
    @Prop({
        type: Function
    })
    readonly optionLoader!: () => Promise<Array<Option>>

    /**
     * Key to compare options objects
     */
    @Prop({
        type: String,
        default: 'id'
    })
    readonly optionTrackBy!: keyof Option

    /**
     * Label to look for in option Object
     */
    @Prop({
        type: String,
        default: 'label'
    })
    readonly optionLabel!: keyof Option

    /**
     * Function to interpolate the custom option label
     */
    @Prop({
        type: Function
    })
    readonly optionCustomLabel!: CustomLabelFunction<Option>

    /**
     * Loads options from API on search change.
     */
    @Prop({
        type: Boolean,
        default: false
    })
    readonly ajaxSearch!: boolean

    @Prop({
        type: Number,
        default: 200
    })
    readonly debounceMillis!: number

    /// ASSOCIATIONS PROPS ///

    /**
     * Array of available relationships.
     */
    @Prop({
        type: Array,
        required: true
    })
    readonly relationships!: Array<Relationship>

    /**
     * Key to compare relationships objects
     */
    @Prop({
        type: String,
        required: true
    })
    readonly relationshipTrackBy!: keyof Relationship

    /**
     * Label to look for in relationship Object
     */
    @Prop({
        type: String,
        default: 'label'
    })
    readonly relationshipLabel!: keyof Relationship

    /**
     * Function to interpolate the custom relationship label
     */
    @Prop({
        type: Function,
        default: (relationship: Relationship, label: keyof Relationship): Relationship | Relationship[keyof Relationship] | '' => {
            if (relationship) {
                return label ? relationship[label] : relationship
            }
            return ''
        }
    })
    readonly relationshipCustomLabel!: CustomLabelFunction<Relationship>

    /// TITLES PROPS ///

    @Prop({
        type: String,
        required: true
    })
    readonly title!: string

    @Prop({
        type: String,
        required: true
    })
    readonly optionsTitle!: string

    /// EMITTERS ///

    @Emit('create')
    createRelationship(): Option {
        return this.state.option
    }

    @Emit('delete')
    deleteRelationship(relationship: Relationship): Relationship {
        return relationship
    }

    /// DATA ///

    state: ConfigurationRelationshipsState<Option, Relationship>

    /// COMPUTED PROPERTIES ///

    get modelValidated(): boolean {
        const defaultValidation = !!this.state.option?.[this.optionTrackBy] &&
            !this._isOptionAssociated()
        return !this.allowDuplicatedOptions
            ? defaultValidation
            : !!this.state.option?.[this.optionTrackBy]
    }

    get hasRelationships(): boolean {
        return !this.relationships.length
    }

    constructor() {
        super()
        this.state = initialConfigurationRelationshipsState()
    }

    private _getAssociatedOption(relationship: Relationship): Option {
        return relationship[this.relationshipTrackBy] as unknown as Option
    }

    private _isOptionAssociated(): boolean {
        return !!this.relationships.find(relationship => {
            const associatedOption: Option = this._getAssociatedOption(relationship)
            return associatedOption[this.optionTrackBy] === this.state.option[this.optionTrackBy]
        })
    }

    /**
     * Returns empty string when relationships is null/undefined
     * If has a relationship label, returns the relationshipCustomLabel() results and casts it to string.
     * Otherwise, returns the optionCustomLabel() results and casts it to string.
     *
     * @param relationship
     * @returns {Object||String}
     */
    getRelationshipLabel(relationship: Relationship): Relationship | Relationship[keyof Relationship] | Option | Option[keyof Option] | '' {
        if (relationship) {
            const props = this.$options.propsData
            if (props) {
                const propsKeys = Object.keys(props)
                if (propsKeys.includes('relationshipLabel') || propsKeys.includes('relationshipCustomLabel')) {
                    // uses a custom label from the relationship
                    return this.relationshipCustomLabel(relationship, this.relationshipLabel)
                } else {
                    // uses the option label associated to the relationship
                    const option: Option = this._getAssociatedOption(relationship)
                    if (option) {
                        return this.optionLabel ? option[this.optionLabel] : option
                    }
                }
            }
        }
        return ''
    }
}
